import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { SearchablePicker } from "@/components/ui/searchable-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { QuickFornecedorForm, QuickCategoriaForm } from "./QuickForms";

export function LancamentoForm({ tipo, item, centrosCusto, contas, categorias = [], bancos = [], onSuccess, onCancel, onCategoriaCreated }) {
  const [loading, setLoading] = useState(false);
  const isEdit = !!item;
  const isPago = isEdit && item?.status === "pago";
  const [fornecedores, setFornecedores] = useState([]);
  const [contratos, setContratos] = useState([]);
  // Quick-cadastro inline
  const [quickFornecedor, setQuickFornecedor] = useState({ open: false, nome: "" });
  const [quickCategoria, setQuickCategoria] = useState({ open: false, nome: "" });

  const reloadFornecedores = async () => {
    try {
      const r = await axios.get(`${API}/clientes-fornecedores`);
      setFornecedores(r.data || []);
    } catch {/* silencioso */}
  };
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    descricao: "",
    valor: 0,
    tipo: tipo,
    conta_id: "",
    centro_custo_id: "",
    categoria_id: "",
    fornecedor_id: "",
    banco_id: "",
    forma_pagamento: "",
    documento: "",
    data_vencimento: new Date().toISOString().split('T')[0],
    data_pagamento: "",
    juros: 0,
    multa: 0,
    desconto: 0,
    observacao: "",
    juros_valor_dia: "",
    multa_valor: "",
    status: "pendente",
    contrato_id: "",
    parcela: "",
    // ---- modo de geracao (so na criacao) ----
    modo: "simples",
    parcelas: 2,
    intervalo_dias: 30,
    dia_fixo: "",
    valor_eh_total: true
  });

  // Carrega clientes/fornecedores para o select (despesa: fornecedor, receita: cliente)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/clientes-fornecedores`);
        if (alive) setFornecedores(r.data || []);
      } catch {/* silencioso */}
      if (isEdit) {
        try {
          const rc = await axios.get(`${API}/contratos`);
          if (alive) setContratos(rc.data || []);
        } catch {/* silencioso */}
      }
    })();
    return () => { alive = false; };
  }, [isEdit]);

  useEffect(() => {
    if (item) {
      setFormData((prev) => ({
        ...prev,
        data: item.data?.split('T')[0] || new Date().toISOString().split('T')[0],
        descricao: item.descricao || "",
        valor: item.valor || 0,
        tipo: item.tipo || tipo,
        conta_id: item.conta_id || "",
        centro_custo_id: item.centro_custo_id || "",
        categoria_id: item.categoria_id || "",
        fornecedor_id: item.fornecedor_id || "",
        banco_id: item.banco_id || "",
        forma_pagamento: item.forma_pagamento || "",
        documento: item.documento || "",
        juros_valor_dia: item.juros_valor_dia ?? "",
        multa_valor: item.multa_valor ?? "",
        data_vencimento: item.data_vencimento?.split('T')[0] || new Date().toISOString().split('T')[0],
        data_pagamento: item.data_pagamento?.split('T')[0] || "",
        juros: item.juros || 0,
        multa: item.multa || 0,
        desconto: item.desconto || 0,
        observacao: item.observacao || "",
        status: item.status || "pendente",
        contrato_id: item.contrato_id || "",
        parcela: item.parcela || "",
        modo: "simples"
      }));
    }
  }, [item, tipo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const pctFix = (obj) => {
        for (const k of ["juros_valor_dia", "multa_valor"]) {
          if (obj[k] === "" || obj[k] == null) obj[k] = null;
          else obj[k] = parseFloat(obj[k]) || 0;
        }
      };
      if (isEdit) {
        // Edicao: PUT parcial (LancamentoUpdate). Nao envia campos de modo.
        const { modo, parcelas, intervalo_dias, dia_fixo, valor_eh_total, ...rest } = formData;
        // contrato_id/parcela vazios nao sobrescrevem vinculo existente
        if (!rest.contrato_id) delete rest.contrato_id;
        if (!rest.parcela) delete rest.parcela;
        // Campos de pagamento (data, juros, multa, desconto) so se aplicam a lancamentos pagos
        if (!isPago) {
          delete rest.data_pagamento;
          delete rest.juros;
          delete rest.multa;
          delete rest.desconto;
        } else {
          rest.juros = parseFloat(rest.juros) || 0;
          rest.multa = parseFloat(rest.multa) || 0;
          rest.desconto = parseFloat(rest.desconto) || 0;
          if (!rest.data_pagamento) delete rest.data_pagamento;
        }
        pctFix(rest);
        await axios.put(`${API}/lancamentos/${item.id}`, rest);
        toast.success("Lançamento atualizado!");
      } else {
        // Criacao: envia tudo, incluindo modo
        const payload = { ...formData };
        if (payload.dia_fixo === "" || payload.dia_fixo == null) {
          delete payload.dia_fixo;
        } else {
          payload.dia_fixo = parseInt(payload.dia_fixo) || null;
        }
        payload.parcelas = parseInt(payload.parcelas) || 1;
        payload.intervalo_dias = parseInt(payload.intervalo_dias) || 30;
        pctFix(payload);
        const r = await axios.post(`${API}/lancamentos`, payload);
        const created = r.data?.created || 1;
        if (created > 1) toast.success(`${created} lançamentos criados (${formData.modo})`);
        else toast.success("Lançamento criado!");
      }
      onSuccess();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao salvar"));
    } finally {
      setLoading(false);
    }
  };

  const modoCfg = {
    simples:    { label: "Simples",    desc: "1 único lançamento" },
    parcelado:  { label: "Parcelado",  desc: "Fornecedor faturou em X parcelas. Valor será dividido." },
    recorrente: { label: "Recorrente", desc: "Aluguel, energia, internet... mesmo valor repetido por N meses." },
  };

  const valorPreview = (() => {
    const v = parseFloat(formData.valor) || 0;
    const p = parseInt(formData.parcelas) || 1;
    if (formData.modo === "parcelado" && formData.valor_eh_total && p > 1) {
      return v / p;
    }
    return v;
  })();

  const FORMAS_PAGAMENTO = [
    { value: "", label: "— Não informada —" },
    { value: "pix", label: "PIX" },
    { value: "boleto", label: "Boleto" },
    { value: "transferencia", label: "Transferência Bancária" },
    { value: "especie", label: "Espécie (Dinheiro)" },
    { value: "cheque", label: "Cheque" },
    { value: "cartao_credito", label: "Cartão de Crédito" },
    { value: "cartao_debito", label: "Cartão de Débito" },
    { value: "deposito", label: "Depósito Bancário" },
    { value: "outro", label: "Outro" },
  ];

  return (
    <>
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden" data-testid="form-lancamento">
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* Selector de modo (apenas na criacao) */}
        {!isEdit && (
          <div className="space-y-2">
            <Label>Tipo de Lançamento</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(modoCfg).map(([k, cfg]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFormData({...formData, modo: k})}
                  className={`p-2 rounded border text-left text-xs transition ${formData.modo === k ? 'border-luvcorp-navy bg-luvcorp-navy/5 ring-1 ring-luvcorp-navy' : 'border-slate-200 hover:border-slate-400'}`}
                  data-testid={`btn-modo-${k}`}
                >
                  <p className="font-semibold text-sm">{cfg.label}</p>
                  <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{cfg.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ====== FORNECEDOR / CLIENTE — destaque no TOPO ====== */}
        <div className="p-3 rounded-lg border border-luvcorp-navy/20 bg-luvcorp-navy/5 space-y-2">
          <Label className="font-semibold text-luvcorp-navy">
            {tipo === "receita" ? "Cliente *" : "Fornecedor / Prestador de Serviços *"}
          </Label>
          <SearchablePicker
            value={formData.fornecedor_id}
            onChange={(value) => setFormData({...formData, fornecedor_id: value})}
            placeholder={`Buscar ${tipo === "receita" ? "cliente" : "fornecedor ou prestador"}...`}
            emptyText="Nenhum cadastrado. Use '+ Cadastrar novo' abaixo."
            options={fornecedores
              .filter(f => {
                // tipo_cadastro: 'cliente' | 'fornecedor' | 'ambos'. Fallback aceita ausente.
                const t = (f.tipo_cadastro || f.tipo || "").toLowerCase();
                if (tipo === "despesa") return t === "fornecedor" || t === "ambos" || t === "" || t === "prestador";
                if (tipo === "receita") return t === "cliente" || t === "ambos" || t === "";
                return true;
              })
              .map(f => ({
                value: f.id,
                label: f.nome || f.razao_social || "(sem nome)",
                sublabel: f.documento || f.nome_fantasia || "",
              }))}
            onCreateNew={(prefill) => setQuickFornecedor({ open: true, nome: prefill || "" })}
            createNewLabel={`+ Cadastrar novo ${tipo === "receita" ? "cliente" : "fornecedor/prestador"}`}
            testId="select-fornecedor-lanc"
          />
        </div>

        {/* ====== VINCULO COM CONTRATO / PARCELA (edicao) ====== */}
        {isEdit && (
          <div className="p-3 rounded-lg border border-sky-200 bg-sky-50/50 space-y-2" data-testid="secao-vinculo-contrato">
            <Label className="font-semibold text-sky-900">Vínculo com Contrato (Nº de Proposta) e Parcela</Label>
            <p className="text-[11px] text-sky-800/80">
              Use para corrigir lançamentos criados manualmente sem vínculo — o Nº de Proposta e a Parcela
              passam a aparecer na listagem de Contas a Receber.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Contrato / Nº de Proposta</Label>
                <SearchablePicker
                  value={formData.contrato_id}
                  onChange={(value) => setFormData({...formData, contrato_id: value})}
                  placeholder="Buscar contrato…"
                  emptyText="Nenhum contrato encontrado."
                  options={contratos.map(c => ({
                    value: c.id,
                    label: c.titulo || c.numero || "(sem número)",
                    sublabel: c.cliente_nome || "",
                  }))}
                  testId="select-contrato-lanc"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Parcela</Label>
                <Input
                  value={formData.parcela}
                  onChange={(e) => setFormData({...formData, parcela: e.target.value})}
                  placeholder="Ex: 5/28 ou Sinal"
                  data-testid="input-parcela-lanc"
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Data do Lançamento *</Label>
            <Input
              type="date"
              value={formData.data}
              onChange={(e) => setFormData({...formData, data: e.target.value})}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>{formData.modo === "simples" ? "Data de Vencimento *" : "1ª Parcela - Vencimento *"}</Label>
            <Input
              type="date"
              value={formData.data_vencimento}
              onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Descrição *</Label>
          <Input
            value={formData.descricao}
            onChange={(e) => setFormData({...formData, descricao: e.target.value})}
            placeholder="Descrição do lançamento"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>
              {formData.modo === "parcelado" && formData.valor_eh_total ? "Valor Total *" : "Valor *"}
            </Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={formData.valor}
              onChange={(e) => setFormData({...formData, valor: parseFloat(e.target.value) || 0})}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Documento</Label>
            <Input
              value={formData.documento}
              onChange={(e) => setFormData({...formData, documento: e.target.value})}
              placeholder="Ex: NF-001"
            />
          </div>
        </div>

        {/* Forma de pagamento + Banco */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Forma de Pagamento</Label>
            <NativeSelect
              value={formData.forma_pagamento}
              onValueChange={(value) => setFormData({...formData, forma_pagamento: value})}
              placeholder="Selecione (PIX, boleto, transferência…)"
              options={FORMAS_PAGAMENTO}
              data-testid="select-forma-pagamento"
            />
          </div>
          <div className="space-y-2">
            <Label>Banco {tipo === "receita" ? "Recebedor" : "Pagador"}</Label>
            <NativeSelect
              value={formData.banco_id}
              onValueChange={(value) => setFormData({...formData, banco_id: value})}
              placeholder="Selecione o banco…"
              options={[
                { value: "", label: "— Não informado —" },
                ...bancos.map(b => ({ value: b.id, label: b.agencia || b.conta ? `${b.nome} (Ag ${b.agencia || "-"} / CC ${b.conta || "-"})` : b.nome }))
              ]}
              data-testid="select-banco-lanc"
            />
          </div>
        </div>

        {/* ====== CORRECAO AUTOMATICA POR ATRASO ====== */}
        {!isPago && (
          <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50 space-y-2" data-testid="secao-correcao-atraso">
            <Label className="font-semibold text-amber-900">Correção automática por atraso (opcional)</Label>
            <p className="text-[11px] text-amber-800/80">
              Após o vencimento, o sistema calcula sozinho o valor corrigido (ex: R$ 2,50 de juros ao dia + R$ 50,00 de multa)
              e a conciliação bancária localiza o valor já corrigido. Nada é gravado no lançamento original.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Juros por atraso (R$ ao dia)</Label>
                <Input
                  type="number" step="0.01" min="0" placeholder="Ex: 2,50"
                  value={formData.juros_valor_dia}
                  onChange={(e) => setFormData({...formData, juros_valor_dia: e.target.value})}
                  data-testid="input-juros-valor-dia"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Multa por atraso (R$ fixo)</Label>
                <Input
                  type="number" step="0.01" min="0" placeholder="Ex: 50,00"
                  value={formData.multa_valor}
                  onChange={(e) => setFormData({...formData, multa_valor: e.target.value})}
                  data-testid="input-multa-valor"
                />
              </div>
            </div>
          </div>
        )}

        {/* ====== DADOS DO PAGAMENTO (apenas lancamentos ja pagos) ====== */}
        {isPago && (() => {
          const base = parseFloat(formData.valor) || 0;
          const j = parseFloat(formData.juros) || 0;
          const m = parseFloat(formData.multa) || 0;
          const d = parseFloat(formData.desconto) || 0;
          const valorPagoCalc = Math.max(0, base + j + m - d);
          return (
            <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/60 space-y-3" data-testid="secao-dados-pagamento">
              <Label className="font-semibold text-blue-900">
                Dados do {tipo === "receita" ? "Recebimento" : "Pagamento"}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Data do {tipo === "receita" ? "Recebimento" : "Pagamento"}</Label>
                  <Input
                    type="date"
                    value={formData.data_pagamento}
                    onChange={(e) => setFormData({...formData, data_pagamento: e.target.value})}
                    data-testid="input-edit-data-pagamento"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Valor {tipo === "receita" ? "Recebido" : "Pago"} (recalculado)</Label>
                  <div className="h-10 px-3 flex items-center rounded-md border bg-white font-mono font-semibold text-blue-900" data-testid="valor-pago-recalculado">
                    {new Intl.NumberFormat('pt-BR', {style:'currency', currency:'BRL'}).format(valorPagoCalc)}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-red-600">Juros (+)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={formData.juros}
                    onChange={(e) => setFormData({...formData, juros: e.target.value})}
                    data-testid="input-edit-juros"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-red-600">Multa (+)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={formData.multa}
                    onChange={(e) => setFormData({...formData, multa: e.target.value})}
                    data-testid="input-edit-multa"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-emerald-700">Desconto (-)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={formData.desconto}
                    onChange={(e) => setFormData({...formData, desconto: e.target.value})}
                    data-testid="input-edit-desconto"
                  />
                </div>
              </div>
              <p className="text-[11px] text-blue-800">
                Ao salvar, o Valor {tipo === "receita" ? "Recebido" : "Pago"} será recalculado automaticamente:
                Valor Original + Juros + Multa − Desconto.
              </p>
            </div>
          );
        })()}

        {/* Bloco de geracao multipla (parcelado/recorrente) */}
        {!isEdit && formData.modo !== "simples" && (
          <div className="p-3 rounded border border-dashed border-slate-300 bg-slate-50 space-y-3" data-testid="bloco-geracao-multipla">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{formData.modo === "recorrente" ? "Repetições" : "Nº de Parcelas"}</Label>
                <Input
                  type="number" min="2" max="120"
                  value={formData.parcelas}
                  onChange={(e) => setFormData({...formData, parcelas: parseInt(e.target.value) || 2})}
                  data-testid="input-parcelas"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Intervalo (dias)</Label>
                <Input
                  type="number" min="1" max="365"
                  value={formData.intervalo_dias}
                  onChange={(e) => setFormData({...formData, intervalo_dias: parseInt(e.target.value) || 30})}
                  disabled={!!formData.dia_fixo}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">OU Dia Fixo (1-31)</Label>
                <Input
                  type="number" min="1" max="31"
                  placeholder="ex: 10"
                  value={formData.dia_fixo}
                  onChange={(e) => setFormData({...formData, dia_fixo: e.target.value})}
                />
              </div>
            </div>
            {formData.modo === "parcelado" && (
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={formData.valor_eh_total}
                  onChange={(e) => setFormData({...formData, valor_eh_total: e.target.checked})}
                />
                O valor digitado é o <b>TOTAL</b> e deve ser dividido entre as parcelas
              </label>
            )}
            <div className="text-xs text-slate-600">
              <p>Pré-visualização: <b>{formData.parcelas}</b> {formData.modo === "recorrente" ? "lançamentos" : "parcelas"} de <b>{new Intl.NumberFormat('pt-BR', {style:'currency', currency:'BRL'}).format(valorPreview)}</b></p>
              {formData.modo === "parcelado" && formData.valor_eh_total && (
                <p className="text-[11px] text-slate-500">Centavos do arredondamento ficam na última parcela.</p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Centro de Custo</Label>
            <NativeSelect
              value={formData.centro_custo_id}
              onValueChange={(value) => setFormData({...formData, centro_custo_id: value})}
              placeholder="Selecione..."
              options={[{value: "", label: "Nenhum"}, ...centrosCusto.map(c => ({ value: c.id, label: `${c.codigo} - ${c.nome}` }))]}
            />
          </div>
          <div className="space-y-2">
            <Label>Conta Contábil</Label>
            <NativeSelect
              value={formData.conta_id}
              onValueChange={(value) => setFormData({...formData, conta_id: value})}
              placeholder="Selecione..."
              options={[{value: "", label: "Nenhum"}, ...contas.map(c => ({ value: c.id, label: `${c.codigo} - ${c.nome}` }))]}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Categoria</Label>
          <SearchablePicker
            value={formData.categoria_id}
            onChange={(value) => setFormData({...formData, categoria_id: value})}
            placeholder="Buscar categoria..."
            emptyText="Nenhuma categoria. Crie uma com '+ Nova categoria'."
            options={categorias
              .filter(c => {
                const t = (c.tipo || "ambos").toLowerCase();
                if (tipo === "despesa") return t === "despesa" || t === "ambos";
                if (tipo === "receita") return t === "receita" || t === "ambos";
                return true;
              })
              .map(c => ({ value: c.id, label: c.nome, sublabel: c.tipo }))}
            onCreateNew={(prefill) => setQuickCategoria({ open: true, nome: prefill || "" })}
            createNewLabel="+ Nova categoria"
            testId="select-categoria-lanc"
          />
        </div>

        <div className="space-y-2">
          <Label>Observação</Label>
          <Textarea
            value={formData.observacao}
            onChange={(e) => setFormData({...formData, observacao: e.target.value})}
            placeholder="Observações adicionais..."
            rows={2}
          />
        </div>
      </div>

      {/* Footer sticky */}
      <div className="flex gap-3 px-6 py-3 border-t bg-white">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button 
          type="submit" 
          className="flex-1 bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
          disabled={loading}
          data-testid="btn-salvar-lancamento"
        >
          {loading ? "Salvando..." : (isEdit ? "Atualizar" : (formData.modo === "simples" ? "Criar Lançamento" : `Criar ${formData.parcelas} ${formData.modo === "recorrente" ? "lançamentos" : "parcelas"}`))}
        </Button>
      </div>
    </form>

    {/* ===== Quick Cadastro: Fornecedor / Cliente ===== */}
    <Dialog open={quickFornecedor.open} onOpenChange={(o) => setQuickFornecedor({ ...quickFornecedor, open: o })}>
      <DialogContent className="max-w-md" data-testid="dialog-quick-fornecedor">
        <DialogHeader>
          <DialogTitle>
            Cadastro rápido — {tipo === "receita" ? "Cliente" : "Fornecedor / Prestador"}
          </DialogTitle>
        </DialogHeader>
        <QuickFornecedorForm
          tipo={tipo}
          nomeInicial={quickFornecedor.nome}
          onSuccess={async (novoId) => {
            setQuickFornecedor({ open: false, nome: "" });
            await reloadFornecedores();
            setFormData((p) => ({ ...p, fornecedor_id: novoId }));
            toast.success("Cadastrado com sucesso!");
          }}
          onCancel={() => setQuickFornecedor({ open: false, nome: "" })}
        />
      </DialogContent>
    </Dialog>

    {/* ===== Quick Cadastro: Categoria ===== */}
    <Dialog open={quickCategoria.open} onOpenChange={(o) => setQuickCategoria({ ...quickCategoria, open: o })}>
      <DialogContent className="max-w-md" data-testid="dialog-quick-categoria">
        <DialogHeader>
          <DialogTitle>Nova categoria</DialogTitle>
        </DialogHeader>
        <QuickCategoriaForm
          tipo={tipo}
          nomeInicial={quickCategoria.nome}
          onSuccess={async (nova) => {
            setQuickCategoria({ open: false, nome: "" });
            if (onCategoriaCreated) await onCategoriaCreated();
            setFormData((p) => ({ ...p, categoria_id: nova.id }));
            toast.success("Categoria criada!");
          }}
          onCancel={() => setQuickCategoria({ open: false, nome: "" })}
        />
      </DialogContent>
    </Dialog>
  </>
  );
}

